import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { Download, Plus, RefreshCw, Search, Truck, UserCheck, Users as UsersIcon, Wallet } from 'lucide-react';
import apiService from '../../services/api';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import useEscapeToClose from '../../hooks/useEscapeToClose';

interface Supplier {
  id: number;
  name: string;
  contactName: string;
  phone: string;
  email: string;
  cuit: string;
  address: string;
  notes: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface SupplierReceipt {
  receiptNumber: string;
  receiptDate: string;
  amount: number;
  note: string;
  items?: Array<{
    ancho?: number;
    largo?: number;
    color?: string;
    microns?: number;
    unidad?: 'bolsas' | 'packs';
    cantidad?: number;
    bolsasPorPack?: number | null;
    totalBolsas?: number;
    unitPrice?: number | null;
    subtotal?: number | null;
    priceIsFigurative?: boolean;
    itemNote?: string | null;
    description?: string;
    loadStock?: boolean;
  }>;
}

const emptyForm = { name: '', contactName: '', phone: '', email: '', cuit: '', address: '', notes: '' };

const StatusBadge: React.FC<{ isActive: boolean }> = ({ isActive }) => (
  <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full border ${isActive ? 'bg-green-100 text-green-700 border-green-200' : 'bg-yellow-100 text-yellow-700 border-yellow-200'}`}>
    {isActive ? 'Activo' : 'Inactivo'}
  </span>
);

const DeleteConfirm: React.FC<{ onConfirm: () => void; onCancel: () => void }> = ({ onConfirm, onCancel }) => (
  <div className="inline-flex items-center gap-2 text-xs">
    <span className="text-gray-600">¿Confirmar?</span>
    <button onClick={onConfirm} className="px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700">Sí</button>
    <button onClick={onCancel} className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-50">No</button>
  </div>
);

// Resumen y buscador ocultos mientras haya pocos proveedores; poner en true para volver a mostrarlos.
const SHOW_SUMMARY_AND_FILTERS = false;

const Proveedores: React.FC = () => {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [actionError, setActionError] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm);
  const [isSavingCreate, setIsSavingCreate] = useState(false);
  const [createError, setCreateError] = useState('');

  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');
  useBodyScrollLock(!!(showCreateModal || editingSupplier));
  useEscapeToClose(showCreateModal, () => setShowCreateModal(false));
  useEscapeToClose(!!editingSupplier, () => setEditingSupplier(null));

  const fetchSuppliers = async () => {
    try {
      setLoadError(null);
      const response = await apiService.getSuppliers();
      setSuppliers(response.suppliers || []);
    } catch (error: any) {
      setLoadError(error?.response?.data?.detail || 'No se pudieron cargar los proveedores.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchSuppliers(); }, []);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return suppliers;
    return suppliers.filter(s =>
      s.name.toLowerCase().includes(term) ||
      s.contactName.toLowerCase().includes(term) ||
      s.email.toLowerCase().includes(term) ||
      s.cuit.toLowerCase().includes(term)
    );
  }, [suppliers, searchTerm]);

  const activeCount = suppliers.filter(s => s.isActive).length;

  const openCreate = () => { setCreateForm(emptyForm); setCreateError(''); setShowCreateModal(true); };
  const submitCreate = async () => {
    if (createForm.name.trim().length < 2) { setCreateError('El nombre es obligatorio.'); return; }
    try {
      setIsSavingCreate(true);
      setCreateError('');
      const response = await apiService.createSupplier(createForm);
      setSuppliers(current => [...current, response.supplier]);
      setShowCreateModal(false);
    } catch (error: any) {
      setCreateError(error?.response?.data?.detail || 'No se pudo crear el proveedor.');
    } finally {
      setIsSavingCreate(false);
    }
  };

  const openEdit = (supplier: Supplier) => {
    setEditingSupplier(supplier);
    setEditForm({ name: supplier.name, contactName: supplier.contactName, phone: supplier.phone, email: supplier.email, cuit: supplier.cuit, address: supplier.address, notes: supplier.notes });
    setEditError('');
  };
  const submitEdit = async () => {
    if (!editingSupplier) return;
    if (editForm.name.trim().length < 2) { setEditError('El nombre es obligatorio.'); return; }
    try {
      setIsSavingEdit(true);
      setEditError('');
      const response = await apiService.updateSupplier(editingSupplier.id, editForm);
      setSuppliers(current => current.map(s => s.id === editingSupplier.id ? response.supplier : s));
      setEditingSupplier(null);
    } catch (error: any) {
      setEditError(error?.response?.data?.detail || 'No se pudo actualizar el proveedor.');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const toggleStatus = async (supplier: Supplier) => {
    try {
      setActionError('');
      const response = await apiService.toggleSupplierStatus(supplier.id);
      setSuppliers(current => current.map(s => s.id === supplier.id ? response.supplier : s));
    } catch (error: any) {
      setActionError(error?.response?.data?.detail || 'No se pudo cambiar el estado.');
    }
  };

  const deleteSupplier = async (id: number) => {
    try {
      setActionError('');
      await apiService.deleteSupplier(id);
      setSuppliers(current => current.filter(s => s.id !== id));
      setConfirmDeleteId(null);
    } catch (error: any) {
      setActionError(error?.response?.data?.detail || 'No se pudo eliminar el proveedor.');
    }
  };

  const exportPurchases = async () => {
    if (!filtered.length) {
      setActionError('No hay proveedores para exportar con la búsqueda actual.');
      return;
    }
    try {
      setIsExporting(true);
      setActionError('');
      const results = await Promise.all(
        filtered.map(async (supplier) => ({
          supplier,
          receipts: (await apiService.getSupplierReceipts(supplier.id)).receipts as SupplierReceipt[],
        }))
      );
      const rows: Array<Record<string, string | number>> = [];
      results.forEach(({ supplier, receipts }) => {
        receipts.forEach((receipt) => {
          if (!receipt.items?.length) {
            rows.push({
              'Proveedor': supplier.name,
              'Fecha': receipt.receiptDate,
              'N° remito': receipt.receiptNumber,
              'Producto / detalle': '',
              'Ancho (cm)': '',
              'Largo (cm)': '',
              'Color': '',
              'Micronaje': '',
              'Unidad': '',
              'Cantidad': '',
              'Bolsas totales': '',
              'Precio unitario': '',
              'Subtotal': '',
              'Precio de referencia': '',
              'Suma al stock': '',
              'Nota del producto': '',
              'Total remito': receipt.amount,
              'Nota del remito': receipt.note || '',
            });
            return;
          }
          receipt.items.forEach((item) => {
            const quantity = Number(item.cantidad || 0);
            const totalBags = Number(item.totalBolsas ?? (item.unidad === 'packs' ? quantity * Number(item.bolsasPorPack || 50) : quantity));
            rows.push({
              'Proveedor': supplier.name,
              'Fecha': receipt.receiptDate,
              'N° remito': receipt.receiptNumber,
              'Producto / detalle': item.description || '',
              'Ancho (cm)': item.ancho ?? '',
              'Largo (cm)': item.largo ?? '',
              'Color': item.color || '',
              'Micronaje': item.microns ?? '',
              'Unidad': item.unidad || '',
              'Cantidad': quantity,
              'Bolsas totales': totalBags,
              'Precio unitario': item.unitPrice ?? '',
              'Subtotal': item.subtotal ?? '',
              'Precio de referencia': item.priceIsFigurative ? 'Sí' : 'No',
              'Suma al stock': item.loadStock === false ? 'No' : 'Sí',
              'Nota del producto': item.itemNote || '',
              'Total remito': receipt.amount,
              'Nota del remito': receipt.note || '',
            });
          });
        });
      });
      if (!rows.length) {
        setActionError('Los proveedores seleccionados todavía no tienen compras cargadas.');
        return;
      }
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const columns = Object.keys(rows[0]);
      worksheet['!cols'] = columns.map((key) => ({
        wch: Math.min(60, Math.max(key.length, ...rows.map((row) => String(row[key] ?? '').length)) + 2),
      }));
      worksheet['!autofilter'] = { ref: worksheet['!ref'] || `A1:${XLSX.utils.encode_col(columns.length - 1)}1` };
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Compras por proveedor');
      XLSX.writeFile(workbook, `compras-por-proveedor-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (error: any) {
      setActionError(error?.response?.data?.detail || 'No se pudieron exportar las compras de proveedores.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Proveedores</h1>
          <p className="text-gray-600">Gestioná tus proveedores para registrar de dónde viene el stock que cargás.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={exportPurchases} disabled={isExporting || isLoading} className="inline-flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"><Download className="h-4 w-4" />{isExporting ? 'Exportando...' : 'Exportar compras'}</button>
          <button onClick={openCreate} className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-3 py-2 text-sm text-white"><Plus className="h-4 w-4" />Nuevo proveedor</button>
          <button onClick={fetchSuppliers} className="inline-flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm"><RefreshCw className="h-4 w-4" />Actualizar</button>
        </div>
      </div>

      {SHOW_SUMMARY_AND_FILTERS && (<>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex items-center gap-3"><Truck className="h-8 w-8 text-primary-600" /><div><p className="text-sm text-gray-500">Total</p><p className="text-xl font-bold">{suppliers.length}</p></div></div>
        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex items-center gap-3"><UserCheck className="h-8 w-8 text-green-600" /><div><p className="text-sm text-gray-500">Activos</p><p className="text-xl font-bold">{activeCount}</p></div></div>
        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex items-center gap-3"><UsersIcon className="h-8 w-8 text-gray-400" /><div><p className="text-sm text-gray-500">Inactivos</p><p className="text-xl font-bold">{suppliers.length - activeCount}</p></div></div>
      </div>

      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Buscar por nombre, contacto, email o CUIT..." className="w-full rounded-lg border border-gray-300 pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" />
        </div>
      </div>
      </>)}

      {loadError && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">{loadError}</div>}
      {actionError && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">{actionError}</div>}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
      <div className="divide-y md:hidden">
        {isLoading ? <div className="px-5 py-6 text-center text-gray-500">Cargando...</div> :
          filtered.map(supplier => (
            <div key={supplier.id} className="p-4 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <button onClick={() => navigate(`/admin/proveedores/${supplier.id}`)} title="Ver cuenta corriente" className="font-medium text-primary-700 hover:underline text-left">{supplier.name}</button>
                <StatusBadge isActive={supplier.isActive} />
              </div>
              <p className="text-sm text-gray-600">{supplier.contactName || '-'}</p>
              <p className="text-sm text-gray-500">{supplier.phone || '-'}{supplier.email ? ` · ${supplier.email}` : ''}</p>
              {supplier.cuit && <p className="text-sm text-gray-500">CUIT: {supplier.cuit}</p>}
              <div className="flex flex-wrap gap-2 pt-1">
                <button onClick={() => navigate(`/admin/proveedores/${supplier.id}`)} className="inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"><Wallet className="h-3.5 w-3.5" />Cuenta</button>
                <button onClick={() => openEdit(supplier)} className="rounded-lg border px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Editar</button>
                <button onClick={() => toggleStatus(supplier)} className="rounded-lg border px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">{supplier.isActive ? 'Desactivar' : 'Activar'}</button>
                {confirmDeleteId === supplier.id ? (
                  <DeleteConfirm onConfirm={() => deleteSupplier(supplier.id)} onCancel={() => setConfirmDeleteId(null)} />
                ) : (
                  <button onClick={() => setConfirmDeleteId(supplier.id)} className="rounded-lg border border-red-200 px-3 py-2 text-sm text-red-600 hover:bg-red-50">Eliminar</button>
                )}
              </div>
            </div>
          ))}
        {!isLoading && !filtered.length && <div className="px-5 py-10 text-center text-gray-500">No se encontraron proveedores.</div>}
      </div>
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr><th className="px-5 py-3">Proveedor</th><th className="px-5 py-3">Contacto</th><th className="px-5 py-3">Teléfono</th><th className="px-5 py-3">Email</th><th className="px-5 py-3">CUIT</th><th className="px-5 py-3">Estado</th><th className="px-5 py-3"></th></tr>
          </thead>
          <tbody className="divide-y">
            {isLoading ? <tr><td colSpan={7} className="px-5 py-6 text-center text-gray-500">Cargando...</td></tr> :
              filtered.map(supplier => (
                <tr key={supplier.id}>
                  <td className="px-5 py-3"><button onClick={() => navigate(`/admin/proveedores/${supplier.id}`)} title="Ver cuenta corriente" className="font-medium text-primary-700 hover:underline text-left">{supplier.name}</button></td>
                  <td className="px-5 py-3">{supplier.contactName || '-'}</td>
                  <td className="px-5 py-3">{supplier.phone || '-'}</td>
                  <td className="px-5 py-3">{supplier.email || '-'}</td>
                  <td className="px-5 py-3">{supplier.cuit || '-'}</td>
                  <td className="px-5 py-3"><StatusBadge isActive={supplier.isActive} /></td>
                  <td className="px-5 py-3">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => navigate(`/admin/proveedores/${supplier.id}`)} className="inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"><Wallet className="h-3.5 w-3.5" />Cuenta</button>
                      <button onClick={() => openEdit(supplier)} className="rounded-lg border px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Editar</button>
                      <button onClick={() => toggleStatus(supplier)} className="rounded-lg border px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">{supplier.isActive ? 'Desactivar' : 'Activar'}</button>
                      {confirmDeleteId === supplier.id ? (
                        <DeleteConfirm onConfirm={() => deleteSupplier(supplier.id)} onCancel={() => setConfirmDeleteId(null)} />
                      ) : (
                        <button onClick={() => setConfirmDeleteId(supplier.id)} className="rounded-lg border border-red-200 px-3 py-2 text-sm text-red-600 hover:bg-red-50">Eliminar</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            {!isLoading && !filtered.length && <tr><td colSpan={7} className="px-5 py-10 text-center text-gray-500">No se encontraron proveedores.</td></tr>}
          </tbody>
        </table>
      </div>
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 sm:p-4">
          <div className="w-full max-w-2xl max-h-[92vh] sm:max-h-[90vh] overflow-y-auto rounded-t-xl sm:rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-6 py-4"><h2 className="text-lg font-semibold">Nuevo proveedor</h2><button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">×</button></div>
            <div className="grid grid-cols-1 gap-4 px-6 py-5 md:grid-cols-2">
              <label className="block"><span className="mb-1 block text-sm font-medium text-gray-700">Nombre *</span><input value={createForm.name} onChange={e => setCreateForm({ ...createForm, name: e.target.value })} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" /></label>
              <label className="block"><span className="mb-1 block text-sm font-medium text-gray-700">Contacto</span><input value={createForm.contactName} onChange={e => setCreateForm({ ...createForm, contactName: e.target.value })} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" /></label>
              <label className="block"><span className="mb-1 block text-sm font-medium text-gray-700">Teléfono</span><input value={createForm.phone} onChange={e => setCreateForm({ ...createForm, phone: e.target.value })} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" /></label>
              <label className="block"><span className="mb-1 block text-sm font-medium text-gray-700">Email</span><input value={createForm.email} onChange={e => setCreateForm({ ...createForm, email: e.target.value })} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" /></label>
              <label className="block"><span className="mb-1 block text-sm font-medium text-gray-700">CUIT</span><input value={createForm.cuit} onChange={e => setCreateForm({ ...createForm, cuit: e.target.value })} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" /></label>
              <label className="block"><span className="mb-1 block text-sm font-medium text-gray-700">Dirección</span><input value={createForm.address} onChange={e => setCreateForm({ ...createForm, address: e.target.value })} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" /></label>
              <label className="block md:col-span-2"><span className="mb-1 block text-sm font-medium text-gray-700">Notas</span><textarea value={createForm.notes} onChange={e => setCreateForm({ ...createForm, notes: e.target.value })} rows={3} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" /></label>
            </div>
            {createError && <div className="mx-6 mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">{createError}</div>}
            <div className="flex justify-end gap-2 border-t px-6 py-4"><button onClick={() => setShowCreateModal(false)} className="rounded-lg border px-4 py-2">Cancelar</button><button onClick={submitCreate} disabled={isSavingCreate} className="rounded-lg bg-primary-600 px-4 py-2 text-white disabled:opacity-50">{isSavingCreate ? 'Guardando...' : 'Crear proveedor'}</button></div>
          </div>
        </div>
      )}

      {editingSupplier && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 sm:p-4">
          <div className="w-full max-w-2xl max-h-[92vh] sm:max-h-[90vh] overflow-y-auto rounded-t-xl sm:rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-6 py-4"><h2 className="text-lg font-semibold">Editar proveedor</h2><button onClick={() => setEditingSupplier(null)} className="text-gray-400 hover:text-gray-600">×</button></div>
            <div className="grid grid-cols-1 gap-4 px-6 py-5 md:grid-cols-2">
              <label className="block"><span className="mb-1 block text-sm font-medium text-gray-700">Nombre *</span><input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" /></label>
              <label className="block"><span className="mb-1 block text-sm font-medium text-gray-700">Contacto</span><input value={editForm.contactName} onChange={e => setEditForm({ ...editForm, contactName: e.target.value })} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" /></label>
              <label className="block"><span className="mb-1 block text-sm font-medium text-gray-700">Teléfono</span><input value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" /></label>
              <label className="block"><span className="mb-1 block text-sm font-medium text-gray-700">Email</span><input value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" /></label>
              <label className="block"><span className="mb-1 block text-sm font-medium text-gray-700">CUIT</span><input value={editForm.cuit} onChange={e => setEditForm({ ...editForm, cuit: e.target.value })} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" /></label>
              <label className="block"><span className="mb-1 block text-sm font-medium text-gray-700">Dirección</span><input value={editForm.address} onChange={e => setEditForm({ ...editForm, address: e.target.value })} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" /></label>
              <label className="block md:col-span-2"><span className="mb-1 block text-sm font-medium text-gray-700">Notas</span><textarea value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} rows={3} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300" /></label>
            </div>
            {editError && <div className="mx-6 mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">{editError}</div>}
            <div className="flex justify-end gap-2 border-t px-6 py-4"><button onClick={() => setEditingSupplier(null)} className="rounded-lg border px-4 py-2">Cancelar</button><button onClick={submitEdit} disabled={isSavingEdit} className="rounded-lg bg-primary-600 px-4 py-2 text-white disabled:opacity-50">{isSavingEdit ? 'Guardando...' : 'Guardar cambios'}</button></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Proveedores;
